/* Client-side draft of the current round's decisions.
 *
 * Screens read the *current* game state (from the API) and overlay this draft to
 * show pending edits; on submit the draft is sent to POST /games/:id/advance and
 * the server resolves the quarter. The draft is keyed to (gameId, round) and
 * resets whenever either changes, so a resolved quarter starts clean.
 */
import { create } from 'zustand';
import type {
  RoundDecisions, MarketingDecision, NewProductDecision, DistMethod,
  Segment, ProductSpec, DraftDecision, FactoryBuild, FactoryOp,
  ContractDecision, GenOp, BrandStrat, WarehouseBuild, LoanTenor, BondTenor,
} from '@venturewrist/shared';
import { NEW_PRODUCT_ID_PREFIX, newProductIndex } from '@venturewrist/shared';

interface DecisionsState {
  gameId: string | null;
  round: number | null;
  draft: RoundDecisions;
  init: (gameId: string, round: number) => void;
  clear: () => void;
  setSupplier: (cat: string, id: string) => void;
  setRnd: (patch: Partial<NonNullable<RoundDecisions['rnd']>>) => void;
  setProductGen: (productId: string, gen: number, patch: { price?: number; production?: number }) => void;
  setProductMkt: (productId: string, mkt: MarketingDecision) => void;
  setBrandStrat: (strat: BrandStrat) => void;
  addNewProduct: (np: NewProductDecision) => void;
  updateNewProduct: (index: number, patch: Partial<NewProductDecision>) => void;
  removeNewProduct: (index: number) => void;
  toggleDistribution: (country: string, method: DistMethod) => void;
  setStoreBuild: (country: string, count: number) => void;
  setStoreClose: (country: string, count: number) => void;
  toggleFlagship: (country: string) => void;
  // Design Studio intents
  setProductName: (productId: string, name: string) => void;
  setTargetSeg: (productId: string, seg: Segment) => void;
  setRetire: (productId: string, retire: boolean) => void;
  setGenSpec: (productId: string, gen: number, spec: ProductSpec) => void;
  setNewGen: (productId: string, newGen: DraftDecision | undefined) => void;
  // Production intents
  setProductAlloc: (productId: string, gen: number, srcId: string, k: number, base: Record<string, number>) => void;
  clearProductAlloc: (productId: string, gen: number) => void;
  setAllocMode: (mode: 'cost' | 'rel') => void;
  addFactoryBuild: (b: FactoryBuild) => void;
  removeFactoryBuild: (index: number) => void;
  setFactoryOp: (id: string, op: FactoryOp['op'] | null) => void;
  addContract: (c: ContractDecision) => void;
  removeContract: (index: number) => void;
  toggleExpansion: (partnerId: string) => void;
  setWhShip: (whId: string, k: number) => void;
  setWhAuto: () => void;
  setGenOp: (productId: string, gen: number, op: GenOp['op'] | null) => void;
  // Logistics intents
  setFreightMode: (mode: 'absorb' | 'passthrough') => void;
  // Financing intents
  addLoan: (tenor: LoanTenor, amount: number) => void;
  removeLoan: (index: number) => void;
  addBond: (tenor: BondTenor, face: number) => void;
  removeBond: (index: number) => void;
  setEquityRaise: (amount: number) => void;
  setBuyback: (amount: number) => void;
  setDivRatio: (ratio: number) => void;
  addWhBuild: (b: WarehouseBuild) => void;
  removeWhBuild: (index: number) => void;
  expandWh: (whId: string, delta: number) => void;
  closeWh: (whId: string) => void;
}

const empty: RoundDecisions = {};

export const useDecisions = create<DecisionsState>((set) => ({
  gameId: null,
  round: null,
  draft: empty,

  init: (gameId, round) =>
    set((s) => (s.gameId === gameId && s.round === round ? s : { gameId, round, draft: {} })),
  clear: () => set((s) => ({ ...s, draft: {} })),

  setSupplier: (cat, id) =>
    set((s) => ({ draft: { ...s.draft, suppliers: { ...s.draft.suppliers, [cat]: id } } })),

  setRnd: (patch) =>
    set((s) => ({ draft: { ...s.draft, rnd: { ...s.draft.rnd, ...patch } } })),

  setProductGen: (productId, gen, patch) =>
    set((s) => {
      const products = [...(s.draft.products ?? [])];
      let p = products.find((x) => x.id === productId);
      if (!p) { p = { id: productId, gens: [] }; products.push(p); }
      const gens = [...(p.gens ?? [])];
      const gi = gens.findIndex((g) => g.gen === gen);
      const next = gi >= 0 ? { ...gens[gi]!, ...patch } : { gen, ...patch };
      // Moving the volume slider re-opens auto-allocation (prototype: gv.alloc=null).
      if (patch.production != null) delete (next as { alloc?: unknown }).alloc;
      if (gi >= 0) gens[gi] = next; else gens.push(next);
      p.gens = gens;
      return { draft: { ...s.draft, products } };
    }),

  setProductAlloc: (productId, gen, srcId, k, base) =>
    set((s) => {
      const products = [...(s.draft.products ?? [])];
      let p = products.find((x) => x.id === productId);
      if (!p) { p = { id: productId, gens: [] }; products.push(p); }
      const gens = [...(p.gens ?? [])];
      const gi = gens.findIndex((g) => g.gen === gen);
      const prev = gi >= 0 ? gens[gi]! : { gen };
      // Seed from the current resolved allocation, then override one source.
      const alloc = { ...(prev.alloc ?? base), [srcId]: Math.max(0, k) };
      // The model's total order follows the sum of its source allocations.
      const production = Math.round(Object.values(alloc).reduce((a, b) => a + (b || 0), 0));
      const next = { ...prev, alloc, production };
      if (gi >= 0) gens[gi] = next; else gens.push(next);
      p.gens = gens;
      return { draft: { ...s.draft, products } };
    }),

  // "Back to auto": stage an explicit `alloc: null`, because a manual split from
  // an earlier quarter lives on the server object and only a null clears it.
  clearProductAlloc: (productId, gen) =>
    set((s) => {
      const products = [...(s.draft.products ?? [])];
      let p = products.find((x) => x.id === productId);
      if (!p) { p = { id: productId, gens: [] }; products.push(p); }
      const gens = [...(p.gens ?? [])];
      const gi = gens.findIndex((g) => g.gen === gen);
      const next = { ...(gi >= 0 ? gens[gi]! : { gen }), alloc: null };
      if (gi >= 0) gens[gi] = next; else gens.push(next);
      p.gens = gens;
      return { draft: { ...s.draft, products } };
    }),

  setAllocMode: (mode) => set((s) => ({ draft: { ...s.draft, allocMode: mode } })),

  addFactoryBuild: (b) =>
    set((s) => ({ draft: { ...s.draft, factoryBuilds: [...(s.draft.factoryBuilds ?? []), b] } })),
  removeFactoryBuild: (index) =>
    set((s) => {
      const arr = [...(s.draft.factoryBuilds ?? [])];
      arr.splice(index, 1);
      return { draft: { ...s.draft, factoryBuilds: arr } };
    }),

  setFactoryOp: (id, op) =>
    set((s) => {
      const arr = (s.draft.factoryOps ?? []).filter((o) => o.id !== id);
      if (op) arr.push({ id, op });
      return { draft: { ...s.draft, factoryOps: arr } };
    }),

  addContract: (c) =>
    set((s) => ({ draft: { ...s.draft, contracts: [...(s.draft.contracts ?? []), c] } })),
  removeContract: (index) =>
    set((s) => {
      const arr = [...(s.draft.contracts ?? [])];
      arr.splice(index, 1);
      return { draft: { ...s.draft, contracts: arr } };
    }),

  toggleExpansion: (partnerId) =>
    set((s) => {
      const arr = [...(s.draft.expansions ?? [])];
      const i = arr.indexOf(partnerId);
      if (i >= 0) arr.splice(i, 1); else arr.push(partnerId);
      return { draft: { ...s.draft, expansions: arr } };
    }),

  setWhShip: (whId, k) =>
    set((s) => ({ draft: { ...s.draft, whAuto: undefined, whAlloc: { ...s.draft.whAlloc, [whId]: Math.max(0, k) } } })),
  setWhAuto: () => set((s) => ({ draft: { ...s.draft, whAlloc: undefined, whAuto: true } })),

  setGenOp: (productId, gen, op) =>
    set((s) => {
      const arr = (s.draft.genOps ?? []).filter((o) => !(o.productId === productId && o.gen === gen));
      if (op) arr.push({ productId, gen, op });
      return { draft: { ...s.draft, genOps: arr } };
    }),

  // ── Logistics ──
  setFreightMode: (mode) => set((s) => ({ draft: { ...s.draft, freightMode: mode } })),

  // ── Financing ──
  addLoan: (tenor, amount) =>
    set((s) => ({ draft: { ...s.draft, financing: { ...s.draft.financing, loans: [...(s.draft.financing?.loans ?? []), { tenor, amount }] } } })),
  removeLoan: (index) =>
    set((s) => { const arr = [...(s.draft.financing?.loans ?? [])]; arr.splice(index, 1); return { draft: { ...s.draft, financing: { ...s.draft.financing, loans: arr } } }; }),
  addBond: (tenor, face) =>
    set((s) => ({ draft: { ...s.draft, financing: { ...s.draft.financing, bonds: [...(s.draft.financing?.bonds ?? []), { tenor, face }] } } })),
  removeBond: (index) =>
    set((s) => { const arr = [...(s.draft.financing?.bonds ?? [])]; arr.splice(index, 1); return { draft: { ...s.draft, financing: { ...s.draft.financing, bonds: arr } } }; }),
  setEquityRaise: (amount) =>
    set((s) => ({ draft: { ...s.draft, financing: { ...s.draft.financing, equityRaise: amount } } })),
  setBuyback: (amount) =>
    set((s) => ({ draft: { ...s.draft, financing: { ...s.draft.financing, buyback: amount } } })),
  setDivRatio: (ratio) =>
    set((s) => ({ draft: { ...s.draft, financing: { ...s.draft.financing, divRatio: ratio } } })),
  addWhBuild: (b) =>
    set((s) => ({ draft: { ...s.draft, whBuilds: [...(s.draft.whBuilds ?? []), b] } })),
  removeWhBuild: (index) =>
    set((s) => {
      const arr = [...(s.draft.whBuilds ?? [])];
      arr.splice(index, 1);
      return { draft: { ...s.draft, whBuilds: arr } };
    }),
  expandWh: (whId, delta) =>
    set((s) => {
      const cur = s.draft.whExpand?.[whId] ?? 0;
      return { draft: { ...s.draft, whExpand: { ...s.draft.whExpand, [whId]: cur + delta } } };
    }),
  closeWh: (whId) =>
    set((s) => {
      const arr = [...(s.draft.whClose ?? [])];
      const i = arr.indexOf(whId);
      if (i >= 0) arr.splice(i, 1); else arr.push(whId);
      return { draft: { ...s.draft, whClose: arr } };
    }),

  setProductMkt: (productId, mkt) =>
    set((s) => {
      const products = [...(s.draft.products ?? [])];
      const i = products.findIndex((x) => x.id === productId);
      if (i >= 0) products[i] = { ...products[i]!, mkt };
      else products.push({ id: productId, mkt });
      return { draft: { ...s.draft, products } };
    }),

  setBrandStrat: (strat) => set((s) => ({ draft: { ...s.draft, brandStrat: strat } })),

  setProductName: (productId, name) =>
    set((s) => ({ draft: { ...s.draft, products: upsertProduct(s.draft, productId, { name }) } })),
  setTargetSeg: (productId, seg) =>
    set((s) => ({ draft: { ...s.draft, products: upsertProduct(s.draft, productId, { targetSeg: seg }) } })),
  setRetire: (productId, retire) =>
    set((s) => ({ draft: { ...s.draft, products: upsertProduct(s.draft, productId, { retire }) } })),
  setNewGen: (productId, newGen) =>
    set((s) => ({ draft: { ...s.draft, products: upsertProduct(s.draft, productId, { newGen }) } })),

  setGenSpec: (productId, gen, spec) =>
    set((s) => {
      const products = [...(s.draft.products ?? [])];
      let p = products.find((x) => x.id === productId);
      if (!p) { p = { id: productId, gens: [] }; products.push(p); }
      const gens = [...(p.gens ?? [])];
      const gi = gens.findIndex((g) => g.gen === gen);
      if (gi >= 0) gens[gi] = { ...gens[gi]!, spec };
      else gens.push({ gen, spec });
      p.gens = gens;
      return { draft: { ...s.draft, products } };
    }),

  addNewProduct: (np) =>
    set((s) => ({ draft: { ...s.draft, newProducts: [...(s.draft.newProducts ?? []), np] } })),
  updateNewProduct: (index, patch) =>
    set((s) => {
      const arr = [...(s.draft.newProducts ?? [])];
      const cur = arr[index];
      if (cur) arr[index] = { ...cur, ...patch };
      return { draft: { ...s.draft, newProducts: arr } };
    }),
  // Other screens address a staged product positionally ('new:<idx>'), so
  // removing one drops its own edits and renumbers the ids above it.
  removeNewProduct: (index) =>
    set((s) => {
      const arr = [...(s.draft.newProducts ?? [])];
      arr.splice(index, 1);
      const products = (s.draft.products ?? [])
        .filter((p) => newProductIndex(p.id) !== index)
        .map((p) => {
          const i = newProductIndex(p.id);
          return i != null && i > index ? { ...p, id: NEW_PRODUCT_ID_PREFIX + (i - 1) } : p;
        });
      return { draft: { ...s.draft, newProducts: arr, products } };
    }),

  toggleDistribution: (country, method) =>
    set((s) => {
      const arr = [...(s.draft.distribution ?? [])];
      const i = arr.findIndex((d) => d.country === country && d.method === method);
      if (i >= 0) arr.splice(i, 1);
      else arr.push({ country, method });
      return { draft: { ...s.draft, distribution: arr } };
    }),

  setStoreBuild: (country, count) =>
    set((s) => {
      const arr = (s.draft.storeBuilds ?? []).filter((b) => b.country !== country);
      if (count > 0) arr.push({ country, count });
      return { draft: { ...s.draft, storeBuilds: arr } };
    }),
  setStoreClose: (country, count) =>
    set((s) => {
      const arr = (s.draft.storeCloses ?? []).filter((b) => b.country !== country);
      if (count > 0) arr.push({ country, count });
      return { draft: { ...s.draft, storeCloses: arr } };
    }),

  toggleFlagship: (country) =>
    set((s) => {
      const arr = [...(s.draft.flagship ?? [])];
      const i = arr.indexOf(country);
      if (i >= 0) arr.splice(i, 1);
      else arr.push(country);
      return { draft: { ...s.draft, flagship: arr } };
    }),
}));

function upsertProduct(
  d: RoundDecisions,
  productId: string,
  patch: Partial<Omit<NonNullable<RoundDecisions['products']>[number], 'id'>>,
): NonNullable<RoundDecisions['products']> {
  const products = [...(d.products ?? [])];
  const i = products.findIndex((x) => x.id === productId);
  if (i >= 0) products[i] = { ...products[i]!, ...patch };
  else products.push({ id: productId, ...patch });
  return products;
}

export const productDecOf = (d: RoundDecisions, productId: string) =>
  d.products?.find((p) => p.id === productId);
export const genDraftOf = (d: RoundDecisions, productId: string, gen: number) =>
  d.products?.find((p) => p.id === productId)?.gens?.find((g) => g.gen === gen);
export const mktDraftOf = (d: RoundDecisions, productId: string) =>
  d.products?.find((p) => p.id === productId)?.mkt;
export const supplierDraftOf = (d: RoundDecisions, cat: string) =>
  (d.suppliers as Record<string, string> | undefined)?.[cat];
export const brandStratDraft = (d: RoundDecisions) => d.brandStrat;
export const freightModeDraft = (d: RoundDecisions) => d.freightMode;
export const whExpandOf = (d: RoundDecisions, whId: string) => d.whExpand?.[whId] ?? 0;
export const whCloseHas = (d: RoundDecisions, whId: string) => !!d.whClose?.includes(whId);
export const financingDraft = (d: RoundDecisions) => d.financing;
export const distHas = (d: RoundDecisions, country: string, method: DistMethod) =>
  !!d.distribution?.some((x) => x.country === country && x.method === method);
export const storeBuildOf = (d: RoundDecisions, country: string) =>
  d.storeBuilds?.find((b) => b.country === country)?.count ?? 0;
export const storeCloseOf = (d: RoundDecisions, country: string) =>
  d.storeCloses?.find((b) => b.country === country)?.count ?? 0;
export const flagshipHas = (d: RoundDecisions, country: string) =>
  !!d.flagship?.includes(country);
export const allocDraftOf = (d: RoundDecisions, productId: string, gen: number) =>
  d.products?.find((p) => p.id === productId)?.gens?.find((g) => g.gen === gen)?.alloc;
export const factoryOpOf = (d: RoundDecisions, id: string) =>
  d.factoryOps?.find((o) => o.id === id)?.op;
export const genOpOf = (d: RoundDecisions, productId: string, gen: number) =>
  d.genOps?.find((o) => o.productId === productId && o.gen === gen)?.op;
export const expansionRequested = (d: RoundDecisions, partnerId: string) =>
  !!d.expansions?.includes(partnerId);

export function pendingCount(d: RoundDecisions): number {
  // count generation-level edits (price/production/alloc/spec) individually
  const genEdits = (d.products ?? []).reduce(
    (a, p) => a + (p.gens?.length ?? 0) + (p.name || p.targetSeg || p.retire || p.mkt || p.newGen ? 1 : 0),
    0,
  );
  return (
    (d.suppliers ? Object.keys(d.suppliers).length : 0) +
    (d.rnd ? Object.keys(d.rnd).length : 0) +
    (d.newProducts?.length ?? 0) +
    genEdits +
    (d.distribution?.length ?? 0) +
    (d.storeBuilds?.length ?? 0) +
    (d.storeCloses?.length ?? 0) +
    (d.flagship?.length ?? 0) +
    (d.allocMode ? 1 : 0) +
    (d.factoryBuilds?.length ?? 0) +
    (d.factoryOps?.length ?? 0) +
    (d.contracts?.length ?? 0) +
    (d.expansions?.length ?? 0) +
    (d.whAlloc ? Object.keys(d.whAlloc).length : 0) +
    (d.whAuto ? 1 : 0) +
    (d.genOps?.length ?? 0) +
    (d.brandStrat ? 1 : 0) +
    (d.freightMode ? 1 : 0) +
    (d.whBuilds?.length ?? 0) +
    (d.whExpand ? Object.keys(d.whExpand).length : 0) +
    (d.whClose?.length ?? 0) +
    ((d.financing?.loans?.length ?? 0) + (d.financing?.bonds?.length ?? 0) +
      (d.financing?.equityRaise ? 1 : 0) + (d.financing?.buyback ? 1 : 0) +
      (d.financing?.divRatio != null ? 1 : 0))
  );
}
